import { useState } from "react";
import axios from "axios";
import { Loader2, ArrowUpRight, PawPrint, Info } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GROOM_OPTIONS = [
  "Full Groom",
  "Bath & Tidy",
  "Puppy's First Groom",
  "Nail Trim & Tidy",
  "De-shed Treatment",
  "Not sure — please recommend",
];

const INITIAL = {
  first_name: "",
  last_name: "",
  phone: "",
  email: "",
  dog_name: "",
  dog_age: "",
  dog_breed: "",
  dog_vet: "",
  groom_type: "",
  message: "",
};

export default function LeadForm() {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const setField = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const required = [
      "first_name",
      "last_name",
      "phone",
      "email",
      "dog_name",
      "dog_age",
      "dog_breed",
      "dog_vet",
      "groom_type",
    ];
    const missing = required.filter((k) => !form[k].trim());
    if (missing.length) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      await axios.post(`${API}/leads`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDone(true);
      toast.success("You're on the waitlist — thank you!");
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Please check your details.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="enquire"
      data-testid="leadform-section"
      className="relative py-24 md:py-32 overflow-hidden"
    >
      <div
        className="lilac-blur"
        style={{ top: "-200px", right: "-200px", opacity: 0.5 }}
      />

      <div className="max-w-6xl mx-auto px-6 md:px-10 relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-32">
            <div className="eyebrow mb-5">— Join The Waitlist</div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-brown-dark leading-[1.05] tracking-tight">
              Add your pup to the <em className="italic text-lilac-dark">waitlist</em>.
            </h2>
            <p className="mt-6 text-lg text-text-secondary leading-relaxed max-w-md">
              Barks &amp; Co is currently operating with limited availability.
              Share a few details below and we'll be in touch as soon as a spot
              opens for your pup.
            </p>

            <div
              className="mt-6 bg-cream border border-[#E6DEC3] rounded-2xl p-5 text-sm text-brown-dark leading-relaxed max-w-md flex gap-3"
              data-testid="leadform-availability-notice"
            >
              <Info size={16} className="text-lilac-dark shrink-0 mt-0.5" />
              <p>
                All fields are required so we can properly match your pup to
                the right service. We aim to get back to you within 48 hours.
              </p>
            </div>

            <a
              href="https://barksco-grooming.breely.com/form/22942"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="leadform-breely-button"
              className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-brown-dark underline underline-offset-4 decoration-lilac-dark hover:text-lilac-dark transition-colors max-w-md"
            >
              Already a client? Request an appointment via our booking form
              <ArrowUpRight size={14} />
            </a>

            <div className="mt-10 hidden lg:flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-lilac-soft flex items-center justify-center">
                <PawPrint size={20} className="text-lilac-dark" />
              </div>
              <div className="text-sm text-brown-warm">
                Replies usually within 48 hours.
                <br />
                <span className="text-text-secondary">No spam — ever.</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            {done ? (
              <div
                data-testid="leadform-success"
                className="bg-cream-alt border border-[#E6DEC3] rounded-[2.5rem] p-10 md:p-14 text-center"
              >
                <div className="h-16 w-16 rounded-full bg-lilac-soft flex items-center justify-center mx-auto mb-6">
                  <PawPrint size={26} className="text-lilac-dark" />
                </div>
                <h3 className="font-serif text-3xl md:text-4xl text-brown-dark">
                  You're on the waitlist.
                </h3>
                <p className="mt-4 text-text-secondary max-w-md mx-auto">
                  Thank you — a confirmation is on its way to your inbox. We'll
                  be in touch personally as soon as a spot becomes available.
                </p>
                <button
                  onClick={() => {
                    setDone(false);
                    setForm(INITIAL);
                  }}
                  data-testid="leadform-reset-button"
                  className="btn-outline mt-8"
                >
                  Submit Another
                </button>
              </div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="leadform-form"
                className="bg-cream-alt border border-[#E6DEC3] rounded-[2.5rem] p-7 md:p-10"
              >
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field
                    label="First name"
                    value={form.first_name}
                    onChange={setField("first_name")}
                    placeholder="Jane"
                    testId="leadform-first-name"
                    required
                  />
                  <Field
                    label="Last name"
                    value={form.last_name}
                    onChange={setField("last_name")}
                    placeholder="Smith"
                    testId="leadform-last-name"
                    required
                  />
                  <Field
                    label="Phone"
                    type="tel"
                    value={form.phone}
                    onChange={setField("phone")}
                    placeholder="021 555 1234"
                    testId="leadform-phone"
                    required
                  />
                  <Field
                    label="Email"
                    type="email"
                    value={form.email}
                    onChange={setField("email")}
                    placeholder="you@example.com"
                    testId="leadform-email"
                    required
                  />
                </div>

                <div className="mt-6 border-t border-[#E6DEC3] pt-6">
                  <div className="eyebrow mb-4">About your pup</div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field
                      label="Dog's name"
                      value={form.dog_name}
                      onChange={setField("dog_name")}
                      placeholder="Coco"
                      testId="leadform-dog-name"
                      required
                    />
                    <Field
                      label="Dog's age"
                      value={form.dog_age}
                      onChange={setField("dog_age")}
                      placeholder="e.g. 6 months, 3 years"
                      testId="leadform-dog-age"
                      required
                    />
                    <Field
                      label="Breed"
                      value={form.dog_breed}
                      onChange={setField("dog_breed")}
                      placeholder="Cavoodle"
                      testId="leadform-dog-breed"
                      required
                    />
                    <Field
                      label="Dog's vet"
                      value={form.dog_vet}
                      onChange={setField("dog_vet")}
                      placeholder="Clinic name / vet"
                      testId="leadform-dog-vet"
                      required
                    />
                  </div>
                </div>

                <div className="mt-6">
                  <label className="block eyebrow mb-2">
                    Type of groom *
                  </label>
                  <Select
                    value={form.groom_type}
                    onValueChange={(v) =>
                      setForm((f) => ({ ...f, groom_type: v }))
                    }
                  >
                    <SelectTrigger
                      data-testid="leadform-groom-trigger"
                      className="w-full bg-cream border border-[#E6DEC3] rounded-full px-5 py-6 text-brown-dark font-sans font-medium focus:ring-1 focus:ring-lilac focus:border-lilac h-auto"
                    >
                      <SelectValue placeholder="Select the service you'd like" />
                    </SelectTrigger>
                    <SelectContent
                      data-testid="leadform-groom-content"
                      className="bg-cream border-[#E6DEC3] rounded-2xl"
                    >
                      {GROOM_OPTIONS.map((c) => (
                        <SelectItem
                          key={c}
                          value={c}
                          data-testid={`leadform-groom-option-${c
                            .replace(/[^a-z0-9]+/gi, "-")
                            .toLowerCase()}`}
                          className="text-brown-dark focus:bg-lilac-soft focus:text-brown-dark cursor-pointer"
                        >
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="mt-6">
                  <label className="block eyebrow mb-2">
                    Anything else we should know? (optional)
                  </label>
                  <textarea
                    value={form.message}
                    onChange={setField("message")}
                    data-testid="leadform-message"
                    rows={3}
                    placeholder="Sensitive areas, behaviour notes, preferred style…"
                    className="w-full bg-cream border border-[#E6DEC3] rounded-3xl px-5 py-4 text-brown-dark placeholder:text-brown-warm/50 font-sans focus:ring-1 focus:ring-lilac focus:border-lilac focus:outline-none transition-all resize-none"
                  />
                </div>

                <div className="mt-8 flex flex-wrap items-center gap-4">
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="leadform-submit-button"
                    className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="animate-spin" size={16} />
                        Sending…
                      </>
                    ) : (
                      <>
                        Join The Waitlist
                        <ArrowUpRight size={16} />
                      </>
                    )}
                  </button>
                  <p className="text-xs text-brown-warm/80">
                    By joining, you're agreeing to be contacted about your
                    pup's appointment.
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, placeholder, testId, required, type = "text" }) {
  return (
    <div>
      <label className="block eyebrow mb-2">
        {label}
        {required ? " *" : ""}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testId}
        required={required}
        className="w-full bg-cream border border-[#E6DEC3] rounded-full px-5 py-3.5 text-brown-dark placeholder:text-brown-warm/50 font-sans focus:ring-1 focus:ring-lilac focus:border-lilac focus:outline-none transition-all"
      />
    </div>
  );
}
